import React from 'react';
import get from 'lodash.get';
import { createDtmHandler } from 'src/universal/utils/analyticsTracking';
import { mapUrlWithDtmLinkQueryWithAnchor } from 'service_modules/models/dtm-tagging';
import { getUpgradeEepForLink } from 'src/universal/utils/orchestraUtils';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import isProduction from 'src/universal/utils/isProduction';
import textRemoveTags from 'service_modules/utils/text-remove-tags';
import { getRubiksDtmProductName } from 'src/universal/utils/cardColorChoices';

export const formatOpenFAQ = contents => {
  const { header, description, content, isHorizontallyStacked, dtm } = contents;
  const newCont = (content || []).map(aContent => {
    return {
      header: aContent.header,
      model: aContent.description
    };
  });
  const newDescription = (description || []).map(desc => {
    return '<p>' + desc + '</p>';
  });
  return {
    isHorizontallyStacked,
    title: header,
    description: newDescription.join(' '),
    model: newCont,
    dtm
  };
};

const mapDtmToLinks = (links, dtmEvents) => {
  return (links || []).map(eachLink => {
    const findSameEvent = dtmEvents[eachLink.key];
    if (findSameEvent) {
      return {
        ...eachLink,
        dtm: findSameEvent
      };
    } else {
      return eachLink;
    }
  });
};

export const getFormattedCtaLinks = ({
  originalLinks,
  pageEep,
  pznOffer,
  dtmEvents,
  productName,
  position,
  colorInfo
}) => {
  const links = mapDtmToLinks(originalLinks, dtmEvents);
  return (links || []).map(link => {
    let productToUse = productName;
    if (colorInfo) {
      productToUse = getRubiksDtmProductName(colorInfo, productName);
    }
    const upgradeEep = getUpgradeEepForLink(link, pznOffer);
    const pznEep = pznOffer.eep;
    const eepToUse = upgradeEep || pznEep || pageEep;
    const { url, dtm } = link;
    const amexHost = isProduction()
      ? 'www.americanexpress.com'
      : 'qwww.americanexpress.com';
    const url2 = replaceStringWithVars({
      varsObject: {
        amexHost: amexHost,
        eep: eepToUse
      },
      string: url
    });
    const dtm2 = dtm
      ? {
          ...dtm,
          tag: replaceStringWithVars({
            varsObject: {
              productName: productToUse,
              position: position
            },
            string: dtm.tag || ''
          }),
          eventAsset: replaceStringWithVars({
            varsObject: { productName: productToUse },
            string: dtm.eventAsset || ''
          })
        }
      : {};
    return {
      ...link,
      dtm: {
        ...link.dtm,
        ...dtm2
      },
      url: mapUrlWithDtmLinkQueryWithAnchor(url2, dtm2)
    };
  });
};

export const buildDisclaimerList = (links, customKeys, cardHeadline) => {
  const keys = customKeys || ['offerTerms', 'ratesFees'];
  return keys.map((linkType = '') => {
    const dtmObj =
      (get(links, `${linkType}.dtm`) && {
        ...links[linkType].dtm,
        eventAsset: `${links[linkType].dtm.eventAsset}`
      }) ||
      {};
    const text = links?.[linkType]?.text ?? '';
    return {
      url: get(links, `${linkType}.url`) || '',
      text: <Sanitizer safelySetInnerHTML={text} />,
      'aria-label': `${textRemoveTags(text)} for ${textRemoveTags(cardHeadline) ??
        ''}`,
      target: '_blank',
      onClick: createDtmHandler({
        dtmObj
      })
    };
  });
};

export const buildModalDataList = (cardList = [], pageEep = '') => {
  return (cardList || []).reduce((arrAcc, card) => {
    const srcObj = card ? card.interstitialToApp : null;
    if (srcObj) {
      const applyCta = srcObj.applyButton || srcObj.button;
      const srcUrl = applyCta.url || '';
      const applyButton = {
        ...applyCta,
        type: 'Primary',
        key: 'applyModalAccept',
        text: 'Continue',
        url: srcUrl.endsWith('=') ? `${srcUrl}${pageEep}` : srcUrl,
        dtm: {
          ...applyCta.dtmTag
        }
      };
      const cancelButton = {
        ...srcObj.cancelButton,
        type: 'Secondary',
        key: 'applyModalCancel',
        text: 'Cancel',
        dtm: {
          ...srcObj.cancelButton.dtmTag
        }
      };
      arrAcc.push({
        modalData: {
          ...srcObj,
          applyButton,
          cancelButton,
          button: undefined
        },
        cardName: card.shortName
      });
    }
    return arrAcc;
  }, []);
};

//Note: For the compare-cards page, the structure of OpenFAQ needs to be the
// same as what's used on the Buisness VAC page. See app/data/icm/Open/Pages/businessCategoryPageData.json in the BE
// Parse code copied from service_modules/xml-http-requests/graphql/data-parser/index.js
export const parseFAQ = faq => {
  if (typeof faq !== 'undefined') {
    faq.description = faq.descriptionArray;

    faq.content.map(item => {
      item.header = item.headerBlock;

      if (item.descriptionBlock) {
        item.descriptionBlock.map(block => {
          if (block && !block.description && block.descriptionArray) {
            block.description = block.descriptionArray;
          }
          return block;
        });
        item.description = item.descriptionBlock;
      }
    });

    faq = formatOpenFAQ(faq);
    faq.model.forEach(faqSection => {
      faqSection.header.headerTagName = 'h2';
      faqSection.model.forEach(faqSubSection => {
        faqSubSection.headerTagName = 'h3';
      });
    });
    return faq;
  }
};

export const makePznEligibleDtm = (pznEligibleDtm, shortName, specialOffers) => {
  const matchedOffer = (specialOffers || []).find(o => o.shortName === shortName);
  const prefixVal = matchedOffer ? 'special_' : '';
  const dtmObj = pznEligibleDtm || {};
  const assetVal = replaceStringWithVars({
    varsObject: { productName: `${prefixVal}${shortName}` },
    string: dtmObj.eventAsset || ''
  });
  return {
    ...dtmObj,
    eventAsset: assetVal
  };
};
